(ns common.schema.entities.workspace.kyc.templates.capman.legal-entity
  (:require
   [common.number-utils :as nu]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.schema.entities.workspace.kyc.kyc-options :as kyc-options]
   [common.schema.entities.workspace.kyc.questions.questions-registry :as questions-registry]
   [common.schema.entities.workspace.kyc.templates.capman.board-member :as board-member]
   [common.schema.entities.workspace.kyc.templates.capman.company-config :as company-config]
   [common.schema.entities.workspace.kyc.templates.capman.pep :as pep]
   [common.schema.entities.workspace.kyc.templates.capman.ubo :as ubo]
   [common.util :as cu]))

(def mandatory-field-meta
  {:mandatory-field? true})

(def ^:private removed-board-member-statuses
  #{:removed-by-controller
    :removed-by-customer})

(defn- board-members-missing-nationality-data?
  [answers]
  (->> (:kyc-legal-entity/board-members-edn answers)
       (remove (fn [board-member]
                 (removed-board-member-statuses
                  (:kyc-board-member/confirmation-status board-member))))
       (some (fn [board-member]
               (kyc-utils/nationalities-missing-data?
                board-member
                kyc-utils/board-member-nationality-keys)))))

;; CapMan-specific signer identification options with custom Finnish wording
(def capman-signer-identification-options
  (concat kyc-options/identification-options
          [{:key          :identification.type/provided-in-previous-questions
            :translations {:en "ID attached in previous steps"
                           :fi "Liitetty aiemmassa kohdassa lomakkeelle"}
            :ord          3.0}]))

(def schema
  {:kyc-template/template-name
   "Legal entity"

   :kyc-template/kyc-form-preface
   company-config/form-preface-text

   :kyc-template/svg-logo
   company-config/svg-logo

   :kyc-template/png-logo
   company-config/png-logo

   :kyc-template/help-email-address
   company-config/contact-email

   :kyc-template/kyc-form-title
   {:translations {:en "Know Your Customer (KYC) form"
                   :fi "Asiakastietokysely"}}

   :kyc.legal-entity/basic-information
   {:ord
    1.0
    :translations
    {:en "Basic information"
     :fi "Perustiedot"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/name mandatory-field-meta]
      [:kyc-legal-entity/jurisdiction mandatory-field-meta]
      [:kyc-legal-entity/business-id mandatory-field-meta]
      [:kyc-legal-entity/trade-register-extract
       {:translations {:en "Trade register extract or equivalent"
                       :fi "Kaupparekisteriote tai vastaava asiakirja"}}]
      [:kyc-legal-entity/legal-form mandatory-field-meta]
      [:kyc-legal-entity/legal-form-other
       {:mandatory-field? (fn [answers]
                            (= :legal-form/other (:kyc-legal-entity/legal-form answers)))}]
      [:kyc-legal-entity/actual-line-of-business mandatory-field-meta]
      [:kyc-legal-entity/actual-line-of-business-other
       {:mandatory-field? (fn [answers]
                            (some #{:industry/other}
                                  (:kyc-legal-entity/actual-line-of-business answers)))}]])}

   :kyc.legal-entity/address-information
   {:ord
    2.0
    :translations
    {:en "Address"
     :fi "Osoite"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/address mandatory-field-meta]
      [:kyc-legal-entity/zip-code mandatory-field-meta]
      [:kyc-legal-entity/city mandatory-field-meta]
      [:kyc-legal-entity/country mandatory-field-meta]
      [:kyc-legal-entity/main-business-address
       {:translations {:en "Main business address (if different from official street address)"
                       :fi "Pääasiallisen liiketoimintapaikan osoite (jos eroaa kotipaikan osoitteesta)"}}]])}

   :kyc.legal-entity/financial-information
   {:ord
    3.0
    :translations
    {:en "Financial information"
     :fi "Pääoma"}
    :info
    {:en (str "<p>Please enclose investor's most recent audited financial accounts and if applicable the annual report.</p>"
              "<p>If the company was recently founded and hence, no financial statements are available, please attach the company's incorporation document.</p>"
              "<p>Source of funds means the origin of the funds invested. It includes the activity and means that generated the funds used in investments. Source of wealth means the origin of total wealth.</p>")
     :fi (str "<p>Liitä lomakkeeseen sijoittajan viimeisin tilintarkastettu tilinpäätös ja/tai vuosikertomus.</p>"
              "<p>Jos yritys on perustettu hiljattain eikä sen tilinpäätöstä ole vielä saatavilla, liitä yrityksen perustamisasiakirja.</p>"
              "<p>Varojen alkuperällä tarkoitetaan sijoitettavien varojen alkuperää. Määritelmään sisältyy toiminta ja keinot, jotka ovat tuottaneet sijoitettavat varat. Varallisuuden alkuperällä tarkoitetaan kokonaisvarallisuuden alkuperää.</p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/financial-records-delivery mandatory-field-meta]
      [:kyc-legal-entity/financial-records-url
       {:mandatory-field? (fn [answers]
                            (= :url (:kyc-legal-entity/financial-records-delivery answers)))}]
      [:kyc-legal-entity/attachment-financial-records
       {:mandatory-field? (fn [answers]
                            (= :attachment (:kyc-legal-entity/financial-records-delivery answers)))}]
      [:kyc-legal-entity/size-of-investment-portfolio mandatory-field-meta]
      [:kyc-legal-entity/split-of-investment-portfolio
       {:mandatory-field? true
        ;; TODO: Must write clj version if want to do server validation in the future
        :validation-fn    (fn [{:keys [answers kyc-process]}]
                            (when-not (-> kyc-process
                                          :kyc-process/status
                                          (= :kyc-process.status/draft))
                              (let [investment-split (get answers :kyc-legal-entity/split-of-investment-portfolio)
                                    total-percentage-num (->> investment-split
                                                              (vals)
                                                              (apply +))
                                    valid-split? (-> total-percentage-num
                                                     (nu/round-decimals 4)
                                                     (= 1.0))]
                                (when-not valid-split?
                                  :validation.error/total-must-equal-100%))))}]
      [:kyc-legal-entity/source-of-funds mandatory-field-meta]
      [:kyc-legal-entity/source-of-funds-allocation-split
       {:mandatory-field? (fn [answers]
                            (not-empty (set (:kyc-legal-entity/source-of-funds answers))))
        ;; TODO: Must write clj version if want to do server validation in the future
        :validation-fn    (fn [{:keys [answers kyc-process]}]
                            (when (and (-> kyc-process
                                           :kyc-process/status
                                           (not= :kyc-process.status/draft))
                                       (not-empty (set (:kyc-legal-entity/source-of-funds answers))))
                              (let [source-split (get answers :kyc-legal-entity/source-of-funds-allocation-split)
                                    total-percentage-num (->> answers
                                                              :kyc-legal-entity/source-of-funds
                                                              (set)
                                                              (map (fn [source]
                                                                     (let [value (get source-split source)]
                                                                       value)))
                                                              (apply +))
                                    valid-split? (-> total-percentage-num
                                                     (nu/round-decimals 4)
                                                     (= 1.0))]
                                (when-not valid-split?
                                  :validation.error/total-must-equal-100%))))}]
      [:kyc-legal-entity/source-of-funds-comment
       {:mandatory-field? (fn [answers]
                            (some (set (:kyc-legal-entity/source-of-funds answers))
                                  [:source-of-funds/other ":source-of-funds/other"
                                   "source-of-funds/other" "other"]))}]
      [:kyc-legal-entity/source-of-wealth
       {:mandatory-field? true}]])}

   :kyc.legal-entity/ownership-structure-and-ubos
   {:ord
    4.0
    :translations
    {:en "Ownership structure"
     :fi "Omistusrakenne"}
    :info
    {:en
     (str
      "If the investor is a limited liability company, please enclose also a certified true copy "
      "of Shareholders’ Register.")
     :fi (str
          "Jos sijoittaja on yksityinen osakeyhtiö, liitä mukaan oikeaksi todistettu kopio yhtiön "
          "osakasluettelosta (sisältäen äänivallan jakautumista koskevat tiedot prosentteina).")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/listed-in-stock-exchange?
       {:mandatory-field? true
        :translations     {:en "Is the investor a publicly listed company?"
                           :fi "Onko sijoittaja listattu pörssiyhtiö?"}}]
      [:kyc-legal-entity/organization-chart-delivery
       {:mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers)))
        :translations     {:en "Ownership structure/chart (determine ownership up to 100%)"
                           :fi "Organisaatiokaavio/rakenne"}}]
      [:kyc-legal-entity/attachment-organization-chart
       {:mandatory-field? (fn [answers]
                            (and (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers))
                                 (= :attachment (:kyc-legal-entity/organization-chart-delivery answers))))
        :translations     {:en "The ownership structure chart (determine ownership up to 100%)"
                           :fi "Organisaatiokaavio/rakenne (ilmoita omistajuus 100% asti)"}}]
      [:kyc-legal-entity/ownership-chart-url
       {:mandatory-field? (fn [answers]
                            (and (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers))
                                 (= :url (:kyc-legal-entity/organization-chart-delivery answers))))}]
      [:kyc-legal-entity/ownership-other
       {:mandatory-field? (fn [answers]
                            (and (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers))
                                 (= :other (:kyc-legal-entity/organization-chart-delivery answers))))}]
      [:kyc-legal-entity/has-agreement-of-voting-rights?
       {:mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers)))
        :translations
        {:en "Have the voting rights been agreed upon so that it differs from ownership?"
         :fi "Onko äänivallasta sovittu omistuksesta poikkeavalla tavalla?"}}]

      [:kyc-legal-entity/shareholder-or-other-agreement-description
       {:mandatory-field? (fn [answers]
                            (and (= :no (:kyc-legal-entity/listed-in-stock-exchange? answers))
                                 (= :yes (:kyc-legal-entity/has-agreement-of-voting-rights? answers))))
        :translations     {:en "Description of voting rights (determine voting rights up to 100%)"
                           :fi "Kuvaus äänivallan jakautumisesta (ilmoita äänivalta 100 % asti)"}}]])}

   :kyc-legal-entity/board-member-data
   {:ord
    5.0
    :translations
    {:en "Board members"
     :fi "Hallituksen jäsenet"}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/board-members-edn {:data/template    board-member/schema
                                            :min-items        2
                                            :mandatory-field? true
                                            :validation-fn
                                            (fn [{:keys [answers kyc-process]}]
                                              (when (and (not= (:kyc-process/status kyc-process)
                                                               :kyc-process.status/draft)
                                                         (board-members-missing-nationality-data?
                                                          answers))
                                                :validation.error/nationality-and-national-id-required))}]])
    :full-width-section? true}

   :kyc-legal-entity/ubo-data
   {:ord
    6.0
    :translations
    {:en "Ultimate beneficial owners (UBO)"
     :fi "Yhteisön tosiasialliset edunsaajat"}

    :info
    {:fi (str "<p>Tosiasialliset edunsaajat, jotka käyttävät määräysvaltaa yhtiössä.</p>"
              "<p>Huomaathan, että jokaisessa yhtiössä on tosiasiallinen edunsaaja.</p>"
              "<p>Luonnollisen henkilön katsotaan käyttävän määräysvaltaa yhteisössä, jos hänellä suoraan tai epäsuorasti on:</p>"

              "<ol>"
              "<li><p>Suurempi kuin 25 % omistusosuus yhteisön osakkeista tai äänivallasta perustuen omistukseen, jäsenyyteen, yhtiöjärjestykseen/-sääntöihin, yhtiösopimukseen tai muuhun sopimukseen tai sääntöihin.</p></li>"
              "<li><p>Oikeus nimittää tai erottaa enemmistö yhteisön hallituksen tai muun vastaavan toimielimen jäsenistä ja, että tällainen oikeus perustuu kohdassa 1. määriteltyihin perusteisiin.</p></li>"
              "</ol>"

              "<p></p>"

              "<p>Mikäli yhteisön oikeudellinen muoto on yhdistys, uskonnollinen yhteisö tai muu yhteisö, jolla ei ole edellä määriteltyjä tosiasiallisia edunsaajia, katsotaan hallituksen jäsenten, (jotka ovat merkittynä yhdistysrekisteriin tai vastaavaan) olevan tosiasiallisia edunsaajia.</p>"

              "<p>Mikäli yhteisö on säätiö (säätiölaki 487/2015), katsotaan hallituksen sekä hallintoneuvoston jäsenten, (jotka ovat merkittynä säätiörekisteriin tai vastaavaan) olevan tosiasiallisia edunsaajia.</p>"
              "<p>Mikäli yhteisössä ei ole kohdissa 1. ja 2. määriteltyjä henkilöitä, katsotaan toimitusjohtajan ja/tai hallituksen jäsenten olevan yhteisön tosiasiallisia edunsaajia ellei yhteisö toisin ilmoita.</p>")

     :en
     (str "<p>Ultimate Beneficial Owners (UBOs) who exercise control over an entity.</p>"
          "<p>Please note that every entity has an UBO. A natural person is considered to have control over an entity, when he/she either directly or indirectly has:</p>"

          "<ol>"

          "<li><p>More than 25% of shares or voting rights based on ownership, membership, articles of association, partnership agreement or corresponding rules or other agreement;</p></li>"
          "<li><p>The right to nominate or dismiss the majority of the board members or members of another corresponding organ of the entity, and such right is based on the same grounds as under subsection (1) above.</p></li>"

          "</ol>"
          "<p></p>"

          "<p>In case the entity is an association (an association defined in Associations Act 503/1989) or a religious community or a similar entity which does not have UBOs defined above, the members of the Board of Directors (members that are registered in the association register or similar) are considered UBOs.</p>"

          "<p>If the entity is a foundation (Foundations Act, 487/2015), the members of the Board of Directors and the members of the Supervisory Board (members that are registered in the foundation register or similar) are considered UBOs.</p>"
          "<p>If the entity is a foreign trust: settlors, trustees, protectors, beneficiaries, classes of beneficiaries and any other natural persons exercising ultimate effective control over the trust are considered UBOs.</p>"
          "<p>If an entity does not have persons referred to in subsections (1) or (2) above, the CEO and the members of the Board of Directors are considered UBOs, unless the entity notifies otherwise.</p>")}

    :conditional-block
    (fn [answers _kyc-process]
      (not= :yes (:kyc-legal-entity/listed-in-stock-exchange? answers)))
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/ubo-edn
       {:data/template ubo/schema
        :info          {:fi "Voit päivittää edunsaajan tietoja klikkaamalla vihreää painiketta nimen edessä."
                        :en "Update beneficiary information by clicking the green button in front of the name."}
        :info-position :below-label}]])}

   :kyc-legal-entity/PEP-data
   {:ord
    7.0
    :translations
    {:en "Politically exposed person (PEP)"
     :fi "Poliittinen vaikutusvalta (PEP)"}
    :info
    {:en
     (str
      "<p>A Politically Exposed Person (PEP) is an individual who currently holds or has "
      "during the past 12 months held a prominent public function, or is an immediate family "
      "member or close associate of such an individual.</p>"
      "<p>Confirm if any of the following are PEPs:"
      "<ul><li>Member of the board</li>"
      "<li>Chief executive officer</li>"
      "<li>Representative (for example the signatories of this form).</li></ul></p>")

     :fi
     (str
      "<p>Poliittisesti vaikutusvaltainen henkilö (PEP) on henkilö, joka tällä hetkellä on tai "
      "on viimeisen 12 kuukauden aikana ollut merkittävässä julkisessa tehtävässä, tai "
      "on tällaisen henkilön perheenjäsen tai läheinen yhteistyökumppani.</p>"
      "<p>Vahvista, ovatko jotkin seuraavista henkilöistä PEP-henkilöitä:"
      "<ul><li>Hallituksen jäsen</li>"
      "<li>Toimitusjohtaja</li>"
      "<li>Edustaja (esimerkiksi tämän lomakkeen allekirjoittajat).</li></ul></p>")}
    :more-info
    {:link-text   {:en "More information"
                   :fi "Lisätietoa täällä"}
     :info-header {:en "Politically exposed person (PEP)"
                   :fi "Poliittinen vaikutusvalta (PEP)"}
     :info-content
     {:en
      (str
       "<p>Politically Exposed Person <strong>(“PEP”)</strong> refers to a person who is or "
       "during <strong>the last 12 months</strong> "
       "has been in a prominent public function, a close associate or an immediate family member "
       "of such a person either in Finland or another country:</p>"
       "<p><strong>A prominent public function is acting as:</strong>"
       "<ul><li>Head of State, Head of Government, minister, deputy or assistant minister</li>"
       "<li>Member of Parliament</li>"
       "<li>member of supreme court, member of constitutional court or member of some "
       "corresponding judicial body whose decisions are not generally subject to further appeal</li>"
       "<li>member of court of auditors or of the highest decision-making body of an "
       "authority auditing the management of Government funds and corresponding to the "
       "State Audit Office</li>"
       "<li>member of the Board of a central bank</li>"
       "<li>ambassador or chargé d’affaires</li>"
       "<li>high-ranking officer (minimum rank of general) in the armed forces</li>"
       "<li>member of the administrative, management or supervisory body of a "
       "state-owned company</li>"
       "<li>Member of the EU Parliament</li>"
       "<li>commissioner</li>"
       "<li>Member of the EU Court of Justice</li>"
       "<li>Member of the EU Court of Auditors</li></ul></p>"
       "<p><strong>The associates of politically exposed persons are:</strong>"
       "<ul><li>Any natural person who is known to have joint beneficial ownership of a "
       "corporation, entrepreneur or legal arrangement, or any other close business relations, "
       "with a politically exposed person</li>"
       "<li>Any natural person who has sole beneficial ownership of a corporation, "
       "entrepreneur or legal arrangement which is known to have been set up for the benefit "
       "of a politically exposed person</li></ul></p>"
       "<p><strong>The immediate family members of politically exposed persons are:</strong>"
       "<ul><li>The spouse or any partner considered equivalent to a spouse by national law;</li>"
       "<li>The children and their spouses or partners referred to above; and</li>"
       "<li>The parents.</li></ul></p>")
      :fi
      (str
       "<p>Poliittisesti vaikutusvaltainen henkilö <strong>(“PEP”)</strong> toimii tai on toiminut "
       "merkittävässä julkisessa virassa, kuuluu tai on kuulunut sellaisen henkilön lähipiiriin "
       "tai on tai on ollut sellaisen henkilön perheenjäsen tai lähipiiriin kuuluva, Suomessa "
       "tai toisessa valtiossa <strong>viimeisen 12 kuukauden aikana</strong>:</p>"
       "<p><strong>Merkittävänä poliittisena virkana pidetään seuraavia virkoja:</strong>"
       "<ul><li>Valtion- tai hallituksen päämies, ministeri, vara-tai apulaisministeri</li>"
       "<li>Parlamentin jäsen</li>"
       "<li>Korkeimman oikeuden, perustuslakituomioistuimen tai muun vastaavan oikeuselimen jäsen</li>"
       "<li>Keskuspankin johtokunnan jäsen</li>"
       "<li>Suurlähettiläs</li>"
       "<li>Puolustusvoimien kenraalikuntaan kuuluva upseeri</li>"
       "<li>Valtionyritysten hallinto-, johto- ja valvontaelimen jäsen</li>"
       "<li>EU-parlamentin jäsen</li>"
       "<li>Komission jäsen</li>"
       "<li>EU-tuomioistuimen jäsen</li>"
       "<li>EU:n tilintarkastustuomioistuimen jäsen</li></ul></p>"
       "<p><strong>Poliittisesti vaikutusvaltaisen henkilön lähipiiriin kuuluvaksi katsotaan:</strong>"
       "<ul><li>Luonnollinen henkilö, joka toimii poliittisesti vaikutusvaltaisen henkilön "
       "yhtiökumppanina</li>"
       "<li>Luonnollinen henkilö, jonka omistaman yhtiön tosiasiallisena edunsaajana on "
       "poliittisesti vaikutusvaltainen henkilö.</li></ul></p>"
       "<p><strong>Poliittisesti vaikutusvaltaisen henkilön lähipiiriin kuuluvaksi katsotaan:</strong>"
       "<ul><li>Poliittisesti vaikutusvaltaisen henkilön puoliso tai puolisoon kansallisen "
       "lain mukaisesti rinnastettava kumppani</li>"
       "<li>Poliittisesti vaikutusvaltaisen henkilön lapset sekä heidän puolisonsa tai puolisoon "
       "kansallisen lain mukaisesti rinnastettava kumppani.</li>"
       "<li>Poliittisesti vaikutusvaltaisen henkilön vanhemmat</li></ul></p>")}}

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/has-peps? {:mandatory-field? true}]
      [:kyc-legal-entity/pep-edn {:data/template pep/schema
                                  :mandatory-field?
                                  (fn [answers]
                                    (= :yes (:kyc-legal-entity/has-peps? answers)))}]])}

   ; Removed as per ticket DEV-5104
   #_:kyc.legal-entity/investor-categorization
   #_{:ord
      8.0
      :translations
      {:en "Investor categorization"
       :fi "Sijoittajaluokitus"}

      :questions
      (questions-registry/resolve-questions
       [[:kyc-legal-entity/professional-investor?]
        [:kyc-legal-entity/professional-investor-criteria]
        [:kyc-legal-entity/large-undertaking-criteria]
        [:kyc-legal-entity/other-professional-investor-type]
        [:kyc-legal-entity/professional-investor-notes]])

      :info
      {:en ""
       :fi ""}}

   :kyc.legal-entity/tax-reporting-information
   {:ord
    8.0
    :translations
    {:en "Tax residency"
     :fi "Verotusmaat"}
    :info
    {:en
     "Please indicate all countries in which the investor is tax resident and provide the associated Tax Identification Number (TIN)."
     :fi
     "Lisää tähän kaikki valtiot, joissa sijoittaja on verovelvollinen sekä veronumero 'TIN' (Tax Identification Number)."}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/tax-countries
       {:mandatory-field? true}]
      [:kyc-legal-entity/usa-tax-residency
       {:mandatory-field? true}]
      [:kyc-legal-entity/usa-tax-residency-yes
       {:mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/usa-tax-residency answers)))}]
      [:kyc-legal-entity/usa-tax-residency-no
       {:mandatory-field? (fn [answers]
                            (= :yes (:kyc-legal-entity/usa-tax-residency answers)))}]])}

   :kyc-legal-entity/crs-classification
   {:ord
    9.0
    :translations
    {:en "Tax reporting"
     :fi "Veroraportointi"}

    :info
    {:en
     (str
      "<p><strong>Financial institution</strong> refers to e.g. a custodial institution, a depository institution, an "
      "investment entity, or a specified insurance company. Please see the relevant tax regulations "
      "for more detailed definitions.</p>"
      "<p><strong>Active Non-Financial Foreign Entity</strong> (Active NFFE) is generally an entity the revenues of "
      "which consist mainly of other business activities than passive investment activity, "
      "such as e.g. production of goods or services.</p>"
      "<p><strong>Passive Non-Financial Foreign Entity</strong> (Passive NFFE) is generally an entity the revenues "
      "of which consist mainly of passive investment activity, so the entity is not engaged in "
      "production of goods or services.</p>")
     :fi
     (str
      "<p><strong>Finanssilaitoksella</strong> tarkoitetaan esimerkiksi säilytysyhteisöä, talletuslaitosta, sijoitusyhteisöä, tai "
      "määriteltyä vakuutusyhtiötä. Katso tarkemmat luokittelumääritelmät soveltuvasta verosääntelystä.</p>"
      "<p><strong>Aktiivinen ulkomainen ei-finanssiyksikkö</strong> Non-Financial Foreign Entity (Active Non-Financial Foreign "
      "Entity, Active NFFE) on yksikkö, jonka tulot koostuvat pääasiassa muusta kuin passiivisesta "
      "sijoitustoiminnasta, eli esimerkiksi tuotteiden tai palveluiden tuottamisesta.</p>"
      "<p><strong>Passiivinen ulkomainen ei-finanssiyksikkö</strong> (Passive Non-Financial Foreign Entity, Passive NFFE) on "
      "yksikkö, jonka tulot koostuvat pääosin passiivisesta sijoitustoiminnasta, ja joka ei valmista "
      "tuotteita tai tuota palveluita.</p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/fatca-specified-us-person?
       {:mandatory-field? true}]
      [:kyc-legal-entity/financial-institution?
       {:mandatory-field? true}]
      [:kyc-legal-entity/fatca-classification
       {:mandatory-field? (fn [answers]
                            (= :yes (:kyc-legal-entity/financial-institution? answers)))}]
      [:kyc-legal-entity/fatca-classification-other
       {:mandatory-field? (fn [answers]
                            (-> answers
                                :kyc-legal-entity/fatca-classification
                                (= :fatca.category/other)))}]
      [:kyc-legal-entity/have-GIIN?
       {:mandatory-field? (fn [answers]
                            (and (not (some #{(:kyc-legal-entity/fatca-classification answers)}
                                            [:fatca.category/exempt-beneficial-owner
                                             :fatca.category/deemed-compliant
                                             :fatca.category/certified-deemed-compliant
                                             :fatca.category/non-participating-fi
                                             :fatca.category/excepted-ffi
                                             :fatca.category/us-person]))
                                 (= :yes (:kyc-legal-entity/financial-institution? answers))))}]
      [:kyc-legal-entity/fatca-GIIN
       {:mandatory-field? (fn [answers]
                            (and (= :yes (:kyc-legal-entity/have-GIIN? answers))
                                 (= :yes (:kyc-legal-entity/financial-institution? answers))))}]
      [:kyc-legal-entity/fatca-no-GIIN
       {:mandatory-field? (fn [answers]
                            (and (= :yes (:kyc-legal-entity/financial-institution? answers))
                                 (= :no (:kyc-legal-entity/have-GIIN? answers))
                                 (< (count (cu/str-trim (:kyc-legal-entity/fatca-GIIN answers))) 1)))}]
      [:kyc-legal-entity/fatca-no-GIIN-other
       {:mandatory-field? (fn [answers]
                            (= :no-giin-reason/other (:kyc-legal-entity/fatca-no-GIIN answers)))}]
      [:kyc-legal-entity/GIIN-Sponsor
       {:mandatory-field? (fn [answers]
                            (= :no-giin-reason/sponsor (:kyc-legal-entity/fatca-no-GIIN answers)))}]
      [:kyc-legal-entity/GIIN-Sponsor-GIIN
       {:mandatory-field? (fn [answers]
                            (= :no-giin-reason/sponsor (:kyc-legal-entity/fatca-no-GIIN answers)))}]
      [:kyc-legal-entity/non-financial-entity-classification
       {:mandatory-field? (fn [answers]
                            (= :no (:kyc-legal-entity/financial-institution? answers)))}]
      [:kyc-legal-entity/non-financial-entity-classification-other
       {:mandatory-field? (fn [answers]
                            (= :non-fatca.category/other
                               (:kyc-legal-entity/non-financial-entity-classification answers)))}]
      [:kyc-legal-entity/crs-classification
       {:mandatory-field? true}]
      [:kyc-legal-entity/crs-classification-other
       {:mandatory-field? (fn [answers]
                            (= :crs-institution/other (:kyc-legal-entity/crs-classification answers)))}]
      [:kyc-legal-entity/crs-tax-reporting-info]])}

   :kyc-legal-entity/sanctions-exposure
   {:ord
    10.0
    :translations
    {:en "International sanctions"
     :fi "Kansainväliset pakotteet"}
    #_:info
    #_{:en
       (str
        "<p>We adhere to United Nations (UN) and European Union (EU) sanctions on a group-wide basis and "
        "comply with United States (US) sanctions laws and regulations generally.</p>"
        "<p>The information captured within the questionnaire "
        "helps us to understand any potential exposure to Sanctions in order to manage our adherence to "
        "the above mentioned programs, laws and regulations.</p>")
       :fi
       (str
        "<p>Sitoudumme noudattamaan YK:n ja EU:n pakotteita konsernitasolla, sekä USA:n pakolakeja ja "
        "-sääntelyä yleisesti.</p>"
        "<p>Lomakkeella annetut tiedot auttavat meitä ymmärtämään mahdollista pakotteille altistumista, "
        "jotta voimme sitoutua edellä mainittujen lakien ja asetusten noudattamiseen.</p>")}
    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/capman-sanctions-confirmation mandatory-field-meta]
      [:kyc-legal-entity/capman-sanctions-cannot-confirm-clarification
       {:conditional-field (fn [answers _]
                             (-> answers
                                 :kyc-legal-entity/capman-sanctions-confirmation
                                 (= :no-i-cannot-confirm)))
        :mandatory-field?  (fn [answers _]
                             (-> answers
                                 :kyc-legal-entity/capman-sanctions-confirmation
                                 (= :no-i-cannot-confirm)))}]]
     #_[[:kyc-legal-entity/sactions-target mandatory-field-meta]
        [:kyc-legal-entity/geographical-exposure mandatory-field-meta]])}

   :kyc-legal-entity/additional-documents
   {:ord
    11.0

    :translations
    {:en "Additional documents"
     :fi "Liitetiedostot"}

    :keep-together?
    true

    :info
    {:en ""
     :fi ""}

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/number-of-documents]
      [:kyc-legal-entity/document-1
       {:mandatory-field? (fn [answers _kyc-process]
                            (some #{(:kyc-legal-entity/number-of-documents answers)}
                                  [:one :two :three :four :five]))}]
      [:kyc-legal-entity/document-2
       {:mandatory-field? (fn [answers _kyc-process]
                            (some #{(:kyc-legal-entity/number-of-documents answers)}
                                  [:two :three :four :five]))}]
      [:kyc-legal-entity/document-3
       {:mandatory-field? (fn [answers _kyc-process]
                            (some #{(:kyc-legal-entity/number-of-documents answers)}
                                  [:three :four :five]))}]
      [:kyc-legal-entity/document-4
       {:mandatory-field? (fn [answers _kyc-process]
                            (some #{(:kyc-legal-entity/number-of-documents answers)}
                                  [:four :five]))}]
      [:kyc-legal-entity/document-5
       {:mandatory-field? (fn [answers _kyc-process]
                            (some #{(:kyc-legal-entity/number-of-documents answers)}
                                  [:five]))}]])}

   :kyc-legal-entity/identifications
   {:ord
    13.0

    :translations
    {:en "Signer identification"
     :fi "Allekirjoittajien tunnistetiedot"}

    :keep-together?
    true

    :info
    {:en (str "<p>True copies must be certified by any third person.</p>"
              "<p>The certification should contain certifier's name, occupation or education, "
              "phone number, email address, date, and signature.</p>")
     :fi (str "<p>Asiakirjojen tulee oikeaksi todistaa kuka tahansa kolmas henkilö.</p>"
              "<p>Todistuksen tulee sisältää oikeaksi todistajan nimi, ammatti tai koulutus, "
              "puhelinnumero, sähköpostiosoite, päivämäärä ja allekirjoitus.</p>")}

    :questions
    (questions-registry/resolve-questions
     (let [old-1st-id-filled-fn (fn [answers _]
                                  (-> answers
                                      :kyc-legal-entity/identification-attachment-1))]
       (concat
        [[:kyc-legal-entity/number-of-signers
          {:mandatory-field? true}]
         [:kyc-legal-entity/signer-name-1
          {:mandatory-field? true}]
         [:kyc-legal-entity/signer-email-1
          {:mandatory-field? true}]
         [:kyc-legal-entity/type-of-identification-attachment-1
          {:mandatory-field? true
           :options          capman-signer-identification-options}]

         ;; removed in DEV-7881
         [:kyc-legal-entity/identification-attachment-1
          {:conditional-field old-1st-id-filled-fn
           :skip-previous-answers-copy? true
           :mandatory-field?  old-1st-id-filled-fn}]]

        (let [signer-1-passport? (fn [answers kyc-process]
                                   (and (not (old-1st-id-filled-fn answers kyc-process))
                                        (-> answers
                                            :kyc-legal-entity/type-of-identification-attachment-1
                                            (= :identification.type/passport))))
              signer-1-id-card? (fn [answers kyc-process]
                                  (and (not (old-1st-id-filled-fn answers kyc-process))
                                       (-> answers
                                           :kyc-legal-entity/type-of-identification-attachment-1
                                           (= :identification.type/id-card))))]
          [[:kyc-legal-entity/identification-national-id-card-front-side-file-1
            {:conditional-field signer-1-id-card?
             :mandatory-field?  signer-1-id-card?
             :translations {:en "Signer 1 - Copy of certified national ID card (front side)"
                            :fi "Allekirjoittaja 1 - Oikeaksi todistettu kopio henkilökortista (etuosa)"}}]

           [:kyc-legal-entity/identification-national-id-card-back-side-file-1
            {:conditional-field signer-1-id-card?
             :mandatory-field?  signer-1-id-card?
             :translations {:en "Signer 1 - Copy of certified national ID card (back side)"
                            :fi "Allekirjoittaja 1 - Oikeaksi todistettu kopio kansallisesta henkilökortista (takaosa)"}}]

           [:kyc-legal-entity/identification-passport-photo-page-file-1
            {:conditional-field signer-1-passport?
             :mandatory-field?  signer-1-passport?
             :translations {:en "Signer 1 - Copy of certified passport"
                            :fi "Allekirjoittaja 1 - Oikeaksi todistettu kopio passista"}}]])

        (let [two-signers? (fn [answers _]
                             (-> answers
                                 :kyc-legal-entity/number-of-signers
                                 (= :two)))
              old-id-filled-fn (fn [answers kyc-process]
                                 (and (two-signers? answers kyc-process)
                                      (-> answers
                                          :kyc-legal-entity/identification-attachment-2)))
              signer-2-passport? (fn [answers kyc-process]
                                   (and (not (old-id-filled-fn answers kyc-process))
                                        (two-signers? answers kyc-process)
                                        (-> answers
                                            :kyc-legal-entity/type-of-identification-attachment-2
                                            (= :identification.type/passport))))
              signer-2-id-card? (fn [answers kyc-process]
                                  (and (not (old-id-filled-fn answers kyc-process))
                                       (two-signers? answers kyc-process)
                                       (-> answers
                                           :kyc-legal-entity/type-of-identification-attachment-2
                                           (= :identification.type/id-card))))]
          [[:kyc-legal-entity/signer-name-2
            {:mandatory-field? two-signers?}]
           [:kyc-legal-entity/signer-email-2
            {:mandatory-field? two-signers?}]
           [:kyc-legal-entity/type-of-identification-attachment-2
            {:mandatory-field? two-signers?
             :options          capman-signer-identification-options}]

           ;; removed in DEV-7881
           [:kyc-legal-entity/identification-attachment-2
            {:skip-previous-answers-copy? true
             :conditional-field old-id-filled-fn
             :mandatory-field?  old-id-filled-fn}]

           [:kyc-legal-entity/identification-national-id-card-front-side-file-2
            {:conditional-field signer-2-id-card?
             :mandatory-field?  signer-2-id-card?
             :translations {:en "Signer 2 - Copy of certified national ID card (front side)"
                            :fi "Allekirjoittaja 2 - Oikeaksi todistettu kopio henkilökortista (etuosa)"}}]

           [:kyc-legal-entity/identification-national-id-card-back-side-file-2
            {:conditional-field signer-2-id-card?
             :mandatory-field?  signer-2-id-card?
             :translations {:en "Signer 2 - Copy of certified national ID card (back side)"
                            :fi "Allekirjoittaja 2 - Oikeaksi todistettu kopio kansallisesta henkilökortista (takaosa)"}}]

           [:kyc-legal-entity/identification-passport-photo-page-file-2
            {:conditional-field signer-2-passport?
             :mandatory-field?  signer-2-passport?
             :translations {:en "Signer 2 - Copy of certified passport"
                            :fi "Allekirjoittaja 2 - Oikeaksi todistettu kopio passista"}}]]))))}

   :kyc-legal-entity/signature-info
   {:ord
    14.0
    :translations
    {:en ""
     :fi ""}

    :end-of-pdf?
    true

    :page-break-before?
    true

    :conditional-block
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-info-1
       {:translations
        {:en (str
              "On behalf of the investor, I hereby confirm that the information I have declared "
              "in this form and in its appendices are true and correct. "
              "I undertake to inform CapMan (kyc@capman.com) within 30 days in case of any "
              "changes to the declared information.")
         :fi (str "Vakuutan, että tässä lomakkeessa ja sen liitteissä olevat tiedot ovat oikeita, "
                  "ja että sijoittaja sitoutuu ilmoittamaan näiden tietojen muutoksista "
                  "CapManille (kyc@capman.com) 30 päivän kuluessa muutoksesta.")}}]
      [:kyc-legal-entity/signature-info-2
       {:translations
        {:en (str "By signing the Client Information Form I understand that the collected personal "
                  "data may be shared and disclosed within the CapMan Group  to customer service, "
                  "other customer relationship management, risk management and marketing purposes. "
                  "CapMan has a right to disclose  personal data to external service providers, "
                  "local tax and financial supervisory authorities in tax reporting "
                  "and supervisory purposes.")
         :fi (str "Allekirjoittamalla tämän lomakkeen ymmärrän, että kerättyä henkilötietoa "
                  "voidaan siirtää ja luovuttaa CapMan-konsernin sisällä asiakaspalveluun "
                  "ja muuhun asiakassuhteen hoitamiseen, riskienhallintaan ja markkinointiin. "
                  "CapManilla voi myös olla oikeus luovuttaa tietoja ulkopuolisille "
                  "palveluntarjoajille sekä Verohallinnolle että Finanssivalvonnalle "
                  "valvonta- ja veroraportointitarkoituksiin.")}}]])}

   :kyc-legal-entity/signature
   {:ord
    15.0

    :translations
    {:en "Signature"
     :fi "Allekirjoitus"}

    :end-of-pdf?
    true

    :conditional-block
    (fn [answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])
           (some #{(:kyc-legal-entity/number-of-signers answers)} [:one :two])))

    :section-not-read-only
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place]
      [:kyc-legal-entity/signature-date]
      [:kyc-legal-entity/signature-name]
      [:kyc-legal-entity/signature-signature]])}

   :kyc-legal-entity/signature-2
   {:ord
    16.0

    :translations
    {:en "Signature 2"
     :fi "Allekirjoitus 2"}

    :end-of-pdf?
    true

    :conditional-block
    (fn [answers kyc-process]
      (and (some #{(:kyc-process/status kyc-process)}
                 [:kyc-process.status/signatures
                  :kyc-process.status/waiting-for-e-sign])
           (some #{(:kyc-legal-entity/number-of-signers answers)} [:two])))

    :section-not-read-only
    (fn [_answers kyc-process]
      (some #{(:kyc-process/status kyc-process)}
            [:kyc-process.status/signatures
             :kyc-process.status/waiting-for-e-sign]))

    :questions
    (questions-registry/resolve-questions
     [[:kyc-legal-entity/signature-place-2]
      [:kyc-legal-entity/signature-date-2]
      [:kyc-legal-entity/signature-name-2]
      [:kyc-legal-entity/signature-signature-2]])}})
